import { useEffect, useState } from "react";
import { CherryPayApi } from "../api/models";
import { useApiClient } from "./useApiClient";

interface UseNotificationRiskRatingOptions {
  businessId: string;
  message: string;
}

export const useNotificationRiskRating = ({
  businessId,
  message,
}: UseNotificationRiskRatingOptions) => {
  const apiClient = useApiClient();
  const [riskLevel, setRiskLevel] =
    useState<CherryPayApi.CherryPassNotificationScheduleReviewRiskLevel | null>(
      null
    );
  const [riskRatingReason, setRiskRatingReason] = useState<string>("");
  const [suggestedMessage, setSuggestedMessage] = useState<string | null>(null);
  const [isReviewingMessage, setIsReviewingMessage] = useState(false);

  // Review message with AI for risk rating
  useEffect(() => {
    if (!message || message.trim() === "") {
      setRiskLevel(null);
      setRiskRatingReason("");
      setSuggestedMessage(null);
      return;
    }

    // Debounce the API call
    const timeoutId = setTimeout(async () => {
      setIsReviewingMessage(true);
      try {
        const result = await apiClient.reviewNotificationMessageWithAI(
          businessId,
          message
        );

        if (result.ok) {
          setRiskLevel(result.data.RiskLevel);
          setRiskRatingReason(result.data.RiskRatingReason);
          setSuggestedMessage(result.data.SuggestedMessage || null);
        }
      } catch (error) {
        // Silently fail - don't show error to user for background check
        console.error("Error reviewing message:", error);
      } finally {
        setIsReviewingMessage(false);
      }
    }, 500); // debounce

    return () => clearTimeout(timeoutId);
  }, [message, businessId, apiClient]);

  return {
    riskLevel,
    riskRatingReason,
    suggestedMessage,
    isReviewingMessage,
  };
};
